import {AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild,} from '@angular/core';
import {LinkService} from '@core/services/link.service';
import {UserService} from '@core/services/user.service';
import {StateService} from '@core/services/state.service';
import {NewsComponentBase} from '@components/news/news-item-base/news-component-base';
import {INewsPost} from '@api/models/news/news';
import {Subscription} from 'rxjs';
import {PageType} from '../../../../../../page-type';
import {TypeService} from '@core/services/type.service';
import {SnackbarService} from '@core/services/snackbar.service';
import {ImageCarouselComponent} from '@components/image/image-carousel/image-carousel.component';
import {DynamicComponentEmbedDirective} from '@shared/modules/shared-news/directives/dynamic-component-embed.directive';
import {LivestreamStatus} from '@api/models/livestream/streaming-info';
import {AnalyticsService} from '@utils/services/analytics/analytics.service';
import {ConsentService} from '@utils/services/shared-consent/consent.service';
import {LibVideoPlayerService} from '@components/video-player/services/lib-video-player.service';
import {EntityType} from '@lib/models/entity/entity-type';
import {FollowService} from '@core/services/follow/follow.service';
import {SubscribeComponent} from '@modules/subscription/pages/subscribe/subscribe.component';
import {SubscriptionContentType} from '@lib/models/subscription/subscription-content-type';
import {SubscriptionIdType} from '@lib/models/subscription/subscription-id-type';
import {SubscriptionTrackingInfo} from '@lib/models/subscription/subscription-tracking-info';

@Component({
  selector: "news-list-item",
  templateUrl: "./news-list-item.component.html",
  styleUrls: [
    "./news-list-item.component.scss",
    "../../../../../../../projects/shared/components/src/lib/news/styles/news-list-item-styles.scss",
  ],
})
export class NewsListItemComponent
  extends NewsComponentBase
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() public lazyLoadImages = false;
  @Input() public preloadFirstImage = false;
  @Input() public showAds = true;
  @Input() public isDetailView = false;
  @Input() public newsPost: INewsPost;
  @Output() public openGallery = new EventEmitter();
  @Output() public openDetail = new EventEmitter();
  @Output() public followTeamSuccess = new EventEmitter();
  @Output() public textRender = new EventEmitter<HTMLElement>();
  public isSmall = StateService.isMobile();
  public isBrowser = StateService.isBrowser;
  public socialMediaType: string;
  public hasPreviewButton = false;
  public teamLink: string;
  public newsCreatorName = "";
  public userIsFollowingTeam = false;
  public isPostedByTeam = true;
  public isTeamPage = true;
  public showTopAd = false;
  public trackingInfo: SubscriptionTrackingInfo = null;
  @ViewChild("imageCarouselComponent")
  private imageCarousel: ImageCarouselComponent;
  // Element that contains fan.at embeddings
  @ViewChild(DynamicComponentEmbedDirective)
  private embeddingElement: DynamicComponentEmbedDirective;
  private user$: Subscription;

  constructor(
    protected changeDetectorRef: ChangeDetectorRef,
    public userService: UserService,
    private followService: FollowService,
    public consentService: ConsentService,
    private appStateService: StateService,
    private snackbar: SnackbarService,
    private analyticsService: AnalyticsService,
    private videoPlayerService: LibVideoPlayerService
  ) {
    super(changeDetectorRef);
  }

  public ngOnInit() {
    this.hasPreviewButton = this.newsPost.info?.button_text?.length > 0;
    this.isPostedByTeam = this.newsPost.creator?.type === "team";
    this.isTeamPage = PageType.Team === TypeService.pageType;
    if (this.isPostedByTeam) {
      this.teamLink = LinkService.getTeamLink(
        this.newsPost.creator.readable_id
      );
    }
    this.showTopAd = this.isDetailView && !this.isPostedByTeam;
    this.newsCreatorName = this.newsPost.creator
      ? this.newsPost.creator.name
      : this.newsPost.author.name;
    this.checkIfUserFollowsTeam();
    if (this.newsPost.media?.facebookURL) {
      this.socialMediaType = "Facebook";
    }
    if (this.newsPost.media?.twitterURL) {
      this.socialMediaType = "Twitter";
    }
    this.newsPost.text = DynamicComponentEmbedDirective.replaceSanitizedTags(
      this.newsPost.text
    );
    this.setupVideo();
    super.init();
  }

  private setupVideo() {
    const info = this.newsPost?.streaming_info;
    if (info?.live_status === LivestreamStatus.Live && StateService.isBrowser) {
      if (info.hls_url) {
        this.newsPost.videos_native.push({
          bm_player_url: info.hls_url,
          paywall: info.paywall,
          liveStream: true,
        });
      }
      if (info.yt_url && this.newsPost?.media) {
        this.newsPost.media.videoURL = info.yt_url;
      }
    }
    if (this.isPostedByTeam) {
      this.videoPlayerService.paywallSettings = {
        activeTeamIds: [this.newsPost.creator.creatorid],
        activeNewsId: this.newsPost._id,
      };
    }
    // Native videos
    if (this.newsPost.videos_native && this.newsPost.videos_native.length > 0) {
      this.newsPost.videos_native.forEach((video) =>
        video.paywall = this.isDetailView ? video.paywall : false
      );
      this.trackingInfo = {
        contentType: SubscriptionContentType.NEWS,
        idType: SubscriptionIdType.NEWS_ID,
        id: this.newsPost._id
      }
    }
  }

  public ngAfterViewInit() {
    if (this.newsPost.info.votingid) {
      this.embeddingElement?.prepareVotingInjection(
        this.newsPost.info.votingid
      );
    }
    this.embeddingElement?.renderEmbeddings(this.newsPost);
  }

  public ngOnDestroy() {
    if (this.user$) {
      this.user$.unsubscribe();
    }
  }

  public onCreatorNameClick(event: MouseEvent): void {
    // Prevent the news entry from opening into detail view when clicking the creator name
    // Does not apply when inside webview because js event needs to be sent on link click
    if (!StateService.isWebview) {
      event.stopPropagation();
    }
  }

  public onImageChange(): void {
    this.analyticsService.sendPageView();
  }

  public onImageClick(index?: number): void {
    if (this.isDetailView && index !== undefined) {
      this.openGallery.emit(index);
      return;
    }
  }

  public onFollowTeam(e) {
    e.stopPropagation();
    e.preventDefault();
    if (!this.userService.currentUser) {
      this.appStateService.set(
        this.appStateService.OPEN_SIGNIN_WINDOW,
        true,
        true
      );
      return;
    }

    this.followTeam();
  }

  private async followTeam() {
      const id = this.newsPost.creator.creatorid;
      await this.followService.toggleFollow(true, EntityType.Team, this.newsCreatorName, id);
      this.followTeamSuccess.emit(this.newsPost.creator.creatorid);
      this.userIsFollowingTeam = true;
  }

  private checkIfUserFollowsTeam(): void {
    this.userIsFollowingTeam = this.userService.currentUser?.isFanOfTeamWithId(
      this.newsPost.creator.creatorid
    );
    this.user$ = this.userService.currentUserSubject.subscribe((user) => {
      this.userIsFollowingTeam = user?.isFanOfTeamWithId(
        this.newsPost.creator.creatorid
      );
    });
  }
}
